/**
 * Direct-to-bucket uploads (#973, specs/pdf-object-storage.md). The Vercel
 * middleware caps an API request body at ~4.5 MB, so file bytes go straight to
 * object storage with presigned PUT URLs; only the small presign and complete
 * calls travel through the API. The presigned URL carries its own
 * authorization, so the PUT sends no credentials.
 */

async function sha256Hex(file) {
  try {
    const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    // Non-secure context or an unreadable file: the hash is optional, the
    // backend verifies size against the bucket either way.
    return null;
  }
}

/** PUT one file to a presigned URL with XHR, for upload progress. */
export function putToPresignedUrl(url, headers, file, onBytes) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    for (const [k, v] of Object.entries(headers || {})) xhr.setRequestHeader(k, v);
    if (onBytes) xhr.upload.onprogress = (e) => { if (e.lengthComputable) onBytes(e.loaded); };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed (HTTP ${xhr.status}): ${(xhr.responseText || '').slice(0, 200)}`));
    };
    xhr.onerror = () => reject(new Error('Upload failed: network error'));
    xhr.send(file);
  });
}

/**
 * Presign and upload files. presignEndpoint answers { uploads: [{name, key,
 * url, headers}] } for { files: [{name, size, type}] } (the dataset
 * upload-urls route). Returns [{ key, name, size, sha256, type }] to hand to
 * the completing call. onProgress gets { loaded, total, file, index, count }.
 */
export async function uploadFilesToStorage(presignEndpoint, files, { onProgress } = {}) {
  const list = Array.from(files);
  const res = await fetch(presignEndpoint, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      files: list.map((f) => ({ name: f.name, size: f.size, type: f.type || 'application/octet-stream' })),
    }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    // Routes answer { error: 'text' } or the { error: { code, message, details } } envelope.
    const err = new Error(body.error?.message || body.error || `HTTP ${res.status}`);
    err.code = body.error?.code;
    err.details = body.error?.details;
    throw err;
  }

  const total = list.reduce((n, f) => n + f.size, 0);
  let done = 0;
  const out = [];
  for (let i = 0; i < list.length; i += 1) {
    const file = list[i];
    const slot = body.uploads?.[i];
    if (!slot || slot.name !== file.name) throw new Error(`No upload URL for ${file.name}`);
    const sha256 = await sha256Hex(file);
    await putToPresignedUrl(slot.url, slot.headers, file, (loaded) => {
      onProgress?.({ loaded: done + loaded, total, file: file.name, index: i, count: list.length });
    });
    done += file.size;
    onProgress?.({ loaded: done, total, file: file.name, index: i, count: list.length });
    out.push({ key: slot.key, name: file.name, size: file.size, sha256, type: file.type || 'application/octet-stream' });
  }
  return out;
}

/** One optional file; null through when there is none. */
export async function uploadFileToStorage(presignEndpoint, file, opts) {
  if (!file) return null;
  const [one] = await uploadFilesToStorage(presignEndpoint, [file], opts);
  return one;
}
